import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import {
  inspecaoService,
  InspecaoSnapshotResponse,
} from "../../../../services/inspecaoService";
import { RankingMetricasPeriodo } from "../types";

interface RankingMetricasQualidadeContextData {
  snapshots: InspecaoSnapshotResponse[] | undefined;
  snapshotsPreviousPeriod: InspecaoSnapshotResponse[] | undefined;
  isLoadingSnapshots: boolean;
  isLoadingSnapshotsPreviousPeriod: boolean;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  selectedDateISO: string;
  setSelectedDateISO: (iso: string) => void;
  selectedPeriod: RankingMetricasPeriodo;
  setSelectedPeriod: (period: RankingMetricasPeriodo) => void;
  selectedPeriodLabel: string;
  comparisonPeriodLabel: string;
  dateLabel: string;
  comparisonDateISO: string;
  comparisonDateLabel: string;
  selectedDateDayMonth: string;
  comparisonDateDayMonth: string;
  selectedLojaNome?: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

const RankingMetricasQualidadeContext = createContext<
  RankingMetricasQualidadeContextData | undefined
>(undefined);

type PeriodRange = {
  start: Date;
  end: Date;
  startISO: string;
  endISO: string;
};

function parseDurationToSeconds(
  value: string | null | undefined
): number | null {
  if (!value) return null;
  const parts = value.split(":");
  if (parts.length !== 3) return null;
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  const seconds = Number(parts[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds;
}

function formatDurationFromSeconds(totalSeconds: number | null): string {
  if (totalSeconds == null) return "00:00:00";
  const normalized = Math.max(0, Math.round(totalSeconds));
  const hours = String(Math.floor(normalized / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((normalized % 3600) / 60)).padStart(2, "0");
  const seconds = String(normalized % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

/** Partes yyyy-MM-dd sempre no calendário civil do dia escolhido (não usar getMonth() do Date local). */
function parseISOYMD(iso: string): { y: number; m: number; d: number } {
  const parts = iso.trim().split("-");
  if (parts.length !== 3) {
    throw new Error(`Data inválida: ${iso}`);
  }
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new Error(`Data inválida: ${iso}`);
  }
  return { y, m, d };
}

/** Dia da semana civil (gregoriano) com meio-dia UTC — igual em qualquer fuso do navegador. */
function civilUtcWeekdaySun0Sat6(y: number, m: number, d: number): number {
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay();
}

/** Quantos dias tem o mês civil (1–12). */
function daysInCalendarMonth(y: number, month1to12: number): number {
  return new Date(Date.UTC(y, month1to12, 0)).getUTCDate();
}

/** Segunda = 0 … domingo = 6 a partir de domingo=0 (getUTCDay). */
function daysSinceMondaySun0(utcDowSun0: number): number {
  return (utcDowSun0 + 6) % 7;
}

function getPeriodRange(
  selectedDateISO: string,
  selectedPeriod: RankingMetricasPeriodo
): PeriodRange {
  if (selectedPeriod === "dia") {
    const anchorDate = BrazilDate.fromISOString(selectedDateISO);
    return {
      start: anchorDate,
      end: anchorDate,
      startISO: selectedDateISO,
      endISO: selectedDateISO,
    };
  }

  if (selectedPeriod === "semana") {
    const { y, m, d } = parseISOYMD(selectedDateISO);
    const dow = civilUtcWeekdaySun0Sat6(y, m, d);
    const offsetToMonday = daysSinceMondaySun0(dow);
    const startISO = BrazilDate.subtractCalendarDaysFromISO(
      selectedDateISO,
      offsetToMonday
    );
    const endISO = BrazilDate.subtractCalendarDaysFromISO(startISO, -6);
    return {
      start: BrazilDate.fromISOString(startISO),
      end: BrazilDate.fromISOString(endISO),
      startISO,
      endISO,
    };
  }

  const { y, m } = parseISOYMD(selectedDateISO);
  const startISO = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastD = daysInCalendarMonth(y, m);
  const endISO = `${y}-${String(m).padStart(2, "0")}-${String(lastD).padStart(2, "0")}`;
  return {
    start: BrazilDate.fromISOString(startISO),
    end: BrazilDate.fromISOString(endISO),
    startISO,
    endISO,
  };
}

function getComparisonRange(
  selectedPeriod: RankingMetricasPeriodo,
  periodRange: PeriodRange
): PeriodRange {
  if (selectedPeriod === "dia") {
    const iso = BrazilDate.subtractCalendarDaysFromISO(periodRange.startISO, 7);
    const previousDate = BrazilDate.fromISOString(iso);
    return {
      start: previousDate,
      end: previousDate,
      startISO: iso,
      endISO: iso,
    };
  }

  if (selectedPeriod === "semana") {
    const startISO = BrazilDate.subtractCalendarDaysFromISO(
      periodRange.startISO,
      7
    );
    const endISO = BrazilDate.subtractCalendarDaysFromISO(
      periodRange.endISO,
      7
    );
    return {
      start: BrazilDate.fromISOString(startISO),
      end: BrazilDate.fromISOString(endISO),
      startISO,
      endISO,
    };
  }

  const { y, m } = parseISOYMD(periodRange.startISO);
  const prevM = m === 1 ? 12 : m - 1;
  const prevY = m === 1 ? y - 1 : y;
  const startISO = `${prevY}-${String(prevM).padStart(2, "0")}-01`;
  const lastD = daysInCalendarMonth(prevY, prevM);
  const endISO = `${prevY}-${String(prevM).padStart(2, "0")}-${String(lastD).padStart(2, "0")}`;
  return {
    start: BrazilDate.fromISOString(startISO),
    end: BrazilDate.fromISOString(endISO),
    startISO,
    endISO,
  };
}

function listDatesInRange(start: Date, end: Date): string[] {
  const startISO = BrazilDate.formatISO(start);
  const endISO = BrazilDate.formatISO(end);
  if (startISO > endISO) {
    return [];
  }
  const dates: string[] = [];
  let curISO = startISO;
  while (true) {
    dates.push(curISO);
    if (curISO === endISO) break;
    curISO = BrazilDate.subtractCalendarDaysFromISO(curISO, -1);
  }
  return dates;
}

function aggregateSnapshotsByRange(
  snapshotsByDate: InspecaoSnapshotResponse[][]
): InspecaoSnapshotResponse[] {
  const totalsByLabel = new Map<
    string,
    {
      lastSnapshot: InspecaoSnapshotResponse;
      quantidadeProducoes: number;
      pizzasAProduzirSum: number | null;
      tempoMedioProducaoSum: number;
      tempoMedioProducaoCount: number;
      tempoMedioAguardandoEntregaSum: number;
      tempoMedioAguardandoEntregaCount: number;
      tempoMedioEmEntregaSum: number;
      tempoMedioEmEntregaCount: number;
    }
  >();

  for (const snapshots of snapshotsByDate) {
    for (const snapshot of snapshots) {
      const row = totalsByLabel.get(snapshot.horario) ?? {
        lastSnapshot: snapshot,
        quantidadeProducoes: 0,
        pizzasAProduzirSum: 0,
        tempoMedioProducaoSum: 0,
        tempoMedioProducaoCount: 0,
        tempoMedioAguardandoEntregaSum: 0,
        tempoMedioAguardandoEntregaCount: 0,
        tempoMedioEmEntregaSum: 0,
        tempoMedioEmEntregaCount: 0,
      };

      row.lastSnapshot = snapshot;
      row.quantidadeProducoes += snapshot.quantidadeProducoes ?? 0;
      if (snapshot.pizzasAProduzir != null) {
        row.pizzasAProduzirSum =
          (row.pizzasAProduzirSum ?? 0) + snapshot.pizzasAProduzir;
      }

      const tempoMedioProducaoSeconds = parseDurationToSeconds(
        snapshot.tempoMedioProducao
      );
      if (tempoMedioProducaoSeconds != null) {
        row.tempoMedioProducaoSum += tempoMedioProducaoSeconds;
        row.tempoMedioProducaoCount += 1;
      }

      const tempoMedioAguardandoEntregaSeconds = parseDurationToSeconds(
        snapshot.tempoMedioAguardandoEntrega
      );
      if (tempoMedioAguardandoEntregaSeconds != null) {
        row.tempoMedioAguardandoEntregaSum +=
          tempoMedioAguardandoEntregaSeconds;
        row.tempoMedioAguardandoEntregaCount += 1;
      }

      const tempoMedioEmEntregaSeconds = parseDurationToSeconds(
        snapshot.tempoMedioEmEntrega
      );
      if (tempoMedioEmEntregaSeconds != null) {
        row.tempoMedioEmEntregaSum += tempoMedioEmEntregaSeconds;
        row.tempoMedioEmEntregaCount += 1;
      }

      totalsByLabel.set(snapshot.horario, row);
    }
  }

  return Array.from(totalsByLabel.values()).map((row) => ({
    ...row.lastSnapshot,
    quantidadeProducoes: row.quantidadeProducoes,
    pizzasAProduzir: row.pizzasAProduzirSum,
    tempoMedioProducao: formatDurationFromSeconds(
      row.tempoMedioProducaoCount > 0
        ? row.tempoMedioProducaoSum / row.tempoMedioProducaoCount
        : null
    ),
    tempoMedioAguardandoEntrega: formatDurationFromSeconds(
      row.tempoMedioAguardandoEntregaCount > 0
        ? row.tempoMedioAguardandoEntregaSum /
            row.tempoMedioAguardandoEntregaCount
        : null
    ),
    tempoMedioEmEntrega: formatDurationFromSeconds(
      row.tempoMedioEmEntregaCount > 0
        ? row.tempoMedioEmEntregaSum / row.tempoMedioEmEntregaCount
        : null
    ),
  }));
}

export const RankingMetricasQualidadeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);
  const [selectedDateISO, setSelectedDateISO] = useState(() =>
    BrazilDate.todayISO()
  );
  const [selectedPeriod, setSelectedPeriod] =
    useState<RankingMetricasPeriodo>("dia");

  const periodRange = useMemo(
    () => getPeriodRange(selectedDateISO, selectedPeriod),
    [selectedDateISO, selectedPeriod]
  );

  const comparisonRange = useMemo(
    () => getComparisonRange(selectedPeriod, periodRange),
    [selectedPeriod, periodRange]
  );

  const selectedPeriodLabel = useMemo(() => {
    if (selectedPeriod === "dia") return "Dia selecionado";
    if (selectedPeriod === "semana") return "Semana atual";
    return "Mês atual";
  }, [selectedPeriod]);

  const comparisonPeriodLabel = useMemo(() => {
    if (selectedPeriod === "dia") return "Semana anterior";
    if (selectedPeriod === "semana") return "Semana passada";
    return "Mês anterior";
  }, [selectedPeriod]);

  const dateLabel = useMemo(() => {
    if (selectedPeriod === "dia") {
      const d = BrazilDate.fromISOString(selectedDateISO);
      return BrazilDate.formatBR(d, false);
    }
    const startLabel = BrazilDate.formatBR(periodRange.start, false);
    const endLabel = BrazilDate.formatBR(periodRange.end, false);
    return `${startLabel} até ${endLabel}`;
  }, [selectedDateISO, selectedPeriod, periodRange]);

  const comparisonDateISO = useMemo(
    () => comparisonRange.startISO,
    [comparisonRange]
  );

  const comparisonDateLabel = useMemo(() => {
    if (selectedPeriod === "dia") {
      const d = BrazilDate.fromISOString(comparisonDateISO);
      return BrazilDate.formatBR(d, false);
    }
    const startLabel = BrazilDate.formatBR(comparisonRange.start, false);
    const endLabel = BrazilDate.formatBR(comparisonRange.end, false);
    return `${startLabel} até ${endLabel}`;
  }, [selectedPeriod, comparisonDateISO, comparisonRange]);

  const selectedDateDayMonth = useMemo(
    () =>
      selectedPeriod === "dia"
        ? BrazilDate.formatDayMonthFromISO(selectedDateISO)
        : selectedPeriod === "semana"
          ? "Semana"
          : "Mês",
    [selectedDateISO, selectedPeriod]
  );

  const comparisonDateDayMonth = useMemo(
    () =>
      selectedPeriod === "dia"
        ? BrazilDate.formatDayMonthFromISO(comparisonDateISO)
        : "Anterior",
    [comparisonDateISO, selectedPeriod]
  );

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaIdParam = useMemo(
    () => (selectedLojaId === undefined ? null : selectedLojaId),
    [selectedLojaId]
  );

  const isSelectedDateToday = selectedDateISO === BrazilDate.todayISO();

  const { data: snapshots, isLoading: isLoadingSnapshots } = useQuery({
    queryKey: [
      "inspecao-snapshots-period",
      selectedPeriod,
      periodRange.startISO,
      periodRange.endISO,
      lojaIdParam,
    ],
    queryFn: async () => {
      if (selectedPeriod === "dia") {
        return inspecaoService.getSnapshots({
          data: selectedDateISO,
          lojaId: lojaIdParam,
        });
      }

      const periodDates = listDatesInRange(periodRange.start, periodRange.end);
      const snapshotsByDate = await Promise.all(
        periodDates.map((dateISO) =>
          inspecaoService.getSnapshots({
            data: dateISO,
            lojaId: lojaIdParam,
          })
        )
      );
      return aggregateSnapshotsByRange(snapshotsByDate);
    },
    refetchInterval: isSelectedDateToday ? 60_000 : false,
  });

  const {
    data: snapshotsPreviousPeriod,
    isLoading: isLoadingSnapshotsPreviousPeriod,
  } = useQuery({
    queryKey: [
      "inspecao-snapshots-period-previous",
      selectedPeriod,
      comparisonRange.startISO,
      comparisonRange.endISO,
      lojaIdParam,
    ],
    queryFn: async () => {
      if (selectedPeriod === "dia") {
        return inspecaoService.getSnapshots({
          data: comparisonDateISO,
          lojaId: lojaIdParam,
        });
      }

      const comparisonDates = listDatesInRange(
        comparisonRange.start,
        comparisonRange.end
      );
      const snapshotsByDate = await Promise.all(
        comparisonDates.map((dateISO) =>
          inspecaoService.getSnapshots({
            data: dateISO,
            lojaId: lojaIdParam,
          })
        )
      );
      return aggregateSnapshotsByRange(snapshotsByDate);
    },
    refetchInterval: isSelectedDateToday ? 60_000 : false,
  });

  const selectedLojaNome = useMemo(
    () => lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  const isLoading = isLoadingLojas;

  return (
    <RankingMetricasQualidadeContext.Provider
      value={{
        snapshots,
        snapshotsPreviousPeriod,
        isLoadingSnapshots,
        isLoadingSnapshotsPreviousPeriod,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        selectedDateISO,
        setSelectedDateISO,
        selectedPeriod,
        setSelectedPeriod,
        selectedPeriodLabel,
        comparisonPeriodLabel,
        dateLabel,
        comparisonDateISO,
        comparisonDateLabel,
        selectedDateDayMonth,
        comparisonDateDayMonth,
        selectedLojaNome,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingMetricasQualidadeContext.Provider>
  );
};

export const useRankingMetricasQualidade =
  (): RankingMetricasQualidadeContextData => {
    const context = useContext(RankingMetricasQualidadeContext);
    if (!context) {
      throw new Error(
        "useRankingMetricasQualidade must be used within RankingMetricasQualidadeProvider"
      );
    }
    return context;
  };
